import React from 'react';
import classnames from 'classnames';
import { get, map, noop, find, isEmpty } from 'lodash';

// local dependencies
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';
import { Icon } from 'modules/icon-bank';
import { getLabel } from 'utils/getLabel';
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { WIDGET_NAMES } from 'constants/entity-names';
import { MemberDetailsHeaderFamilyUnitWidget } from 'ui/containers/MemberDetailsHeaderFamilyUnitWidget';
import { getCallerTypeIcon } from 'resolvers/callStateEntities';
import { MemberDetailAssistiveActionsNavWidget } from 'ui/containers/MemberDetailAssistiveActionsNavWidget';
import { MemberDetailsSavingsWidget } from 'ui/containers/MemberDetailsSavingsWidget';
import { MemberDetailsPayBalanceWidget } from 'ui/containers/MemberDetailsPayBalanceWidget';
import { MemberDetailsAlertsStatsWidget } from 'ui/containers/MemberDetailsAlertsStatsWidget';

// get a global redux store ( creates empty store if not already created )
const store = getStore();

// widget views
const WIDGET_VIEWS = {
    [ WIDGET_NAMES.MEMBER_DETAILS_HEADER_FAMILY_UNIT ]: MemberDetailsHeaderFamilyUnitWidget,
    [ WIDGET_NAMES.MEMBER_DETAILS_ALERTS_STATS ]: MemberDetailsAlertsStatsWidget,
    [ WIDGET_NAMES.MEMBER_DETAILS_SAVINGS ]: MemberDetailsSavingsWidget,
    [ WIDGET_NAMES.MEMBER_DETAILS_PAY_BALANCE ]: MemberDetailsPayBalanceWidget,
    [ WIDGET_NAMES.MEMBER_DETAILS_QUICK_ACTIONS ]: MemberDetailAssistiveActionsNavWidget
};

/*--------------------------- */

// Success View
export const memberDetailsHeaderWidgetSuccess = ( props ) => {

    // extracting caller Type and Caller info from the store
    const { callType: callTypeOptions, callerType: callerTypeOptions } = get( store.getState()[ REDUCER.APP_CONTEXT ], 'component.data.options', {} );

    // finding the caller type and call type by their value in the props
    const selectedCallType = find( callTypeOptions, { value: get( props, 'callerInfo.callType' ) } );
    const selectedCallerType = find( callerTypeOptions, { value: get( props, 'callerInfo.callerType' ) } );
   
    return (
        <div className='ui-member-details-header-widget'>

            {/* top section */}
            <div className='ui-member-details-header-widget__top-section'>

                {/* caller details */}
                {
                    isEmpty( props.callerInfo ) ? null : (
                        <div className='ui-member-details-header-widget__top-section__caller-details'>
                            <div className='ui-member-details-header-widget__top-section__caller-details__name'>
                                <div className='ui-member-details-header-widget__top-section__caller-details__name__icon'>
                                    <Icon name='person' />
                                </div>
                                <p className='ui-member-details-header-widget__top-section__caller-details__name__title'>
                                    { props.callerInfo.callerName }
                                </p>
                            </div>
                            <div className='ui-member-details-header-widget__top-section__caller-details__details'>
                                <div className='ui-member-details-header-widget__top-section__caller-details__details__specific'>
                                    <div className='ui-member-details-header-widget__top-section__caller-details__details__specific__icon'>
                                        <Icon name={ getCallerTypeIcon( get( props, 'callerInfo.callerType' ) ) } />
                                    </div>
                                    <p className='ui-member-details-header-widget__top-section__caller-details__details__specific__title'>
                                        { getLabel( selectedCallerType.title ) }
                                    </p>
                                </div>
                                <div className='ui-member-details-header-widget__top-section__caller-details__details__specific'>
                                    <div className='ui-member-details-header-widget__top-section__caller-details__details__specific__icon'>
                                        <Icon name = 'phoneForward' />
                                    </div>
                                    <p className='ui-member-details-header-widget__top-section__caller-details__details__specific__title'>
                                        { getLabel( selectedCallType.title ) }
                                    </p>
                                </div>
                            </div>
                        </div>
                    )
                }

                {/* widgets */}
                {/* <div className='ui-member-details-header-widget__top-section__widgets'> */}
                    {
                        map( props.widgets, widget => {

                            // get widget view
                            const WidgetView = WIDGET_VIEWS[ widget.name ];

                            // widget class name
                            const widgetClassName = classnames( 'ui-member-details-header-widget__top-section__widgets__widget', `ui-member-details-header-widget__top-section__widgets__widget--name-${ widget.name }` );

                            // if actions are loading, hide other widgets except family unit
                            if( props.isFetchingActions && 'memberDetailsHeaderFamilyUnit' !== widget.name ) {
                                return null;
                            }

                            return (
                                <WidgetView
                                    key={  'memberDetailsHeaderFamilyUnit' === widget.name ? widget.name : `${ widget.name }__${ props.memberId }` }
                                    widget={ widget }
                                    className={ widgetClassName }
                                    memberId={ props.memberId }
                                    onGenerateActionListLog={ props.onGenerateActionListLog }
                                    requestPayload={ props.requestPayload }
                                    onMemberSwitch={ props.onMemberSwitch }
                                />
                            );
                            
                        } )
                    }
                </div>
        
            {/* </div> */}
            
            {/* live member status refresh */}
            <div className='ui-member-details-header-widget__live-status' aria-live='polite' aria-atomic='true'>
                <button
                    className={ `ui-member-details-header-widget__live-status__button${ props.isCheckingLiveMemberStatus ? ' ui-member-details-header-widget__live-status__button--checking' : '' }` }
                    onClick={ props.onCheckLiveMemberStatus }
                    disabled={ props.isCheckingLiveMemberStatus }
                >
                    { props.isCheckingLiveMemberStatus ? 'Checking…' : 'Check current status' }
                </button>
                { props.liveMemberStatusAsOf && ! props.liveMemberStatusError && (
                    <span className='ui-member-details-header-widget__live-status__timestamp'>
                        { `Live as of ${ new Date( props.liveMemberStatusAsOf ).toLocaleString() }` }
                    </span>
                ) }
                { props.liveMemberStatusError === 'FETCH_FAILED' && (
                    <span className='ui-member-details-header-widget__live-status__error'>
                        Unable to fetch live status &mdash; try again
                    </span>
                ) }
                { props.liveMemberStatusError === 'STATUS_UNAVAILABLE' && (
                    <span className='ui-member-details-header-widget__live-status__error'>
                        Status unavailable
                    </span>
                ) }
            </div>

            {/* bottom section */}
            <div className='ui-member-details-header-widget__bottom-section'>
                {
                    props.isFetchingActions && (
                        <div className='ui-member-detail-assistive-actions-nav-widget-preloader'>
                            { map( [ ...Array( 10 ).keys() ], item => {
                                return (
                                    <div key={ item } className='ui-member-detail-assistive-actions-nav-widget-preloader__items'>
                                        <div className='ui-member-detail-assistive-actions-nav-widget-preloader__items__item'></div>
                                        <div className='ui-member-detail-assistive-actions-nav-widget-preloader__items__title'></div>
                                    </div>
                                );
                            } ) }
                        </div>
                    )
                }
            </div>
            
		</div>
	);
};

// set display name
memberDetailsHeaderWidgetSuccess.displayName = 'memberDetailsHeaderWidget';

// set default props
memberDetailsHeaderWidgetSuccess.defaultProps = {
	memberId: null,
	onMemberSwitch: noop,
	callerInfo: {},
};

// prop types
memberDetailsHeaderWidgetSuccess.propTypes = {};

/****************************************/

// preloader
export const memberDetailsHeaderWidgetPreloader = () => {
    return (
        <div className='ui-member-details-header-widget-preloader'>

            {/* member details */}
            <div className='ui-member-details-header-widget-preloader__caller-details'>
                <div className='ui-member-details-header-widget-preloader__caller-details__name'>
                    <div className='ui-member-details-header-widget-preloader__caller-details__name__icon__preloader'></div>
                    <p className='ui-member-details-header-widget-preloader__caller-details__name__title__preloader'></p>
                </div>
                <div className='ui-member-details-header-widget-preloader__caller-details__details'>
                    <div className='ui-member-details-header-widget-preloader__caller-details__details__specific'>
                        <div className='ui-member-details-header-widget-preloader__caller-details__details__specific__icon__preloader'></div>
                        <p className='ui-member-details-header-widget-preloader__caller-details__details__specific__title__preloader'></p>
                    </div>
                    <div className='ui-member-details-header-widget-preloader__caller-details__details__specific'>
                        <div className='ui-member-details-header-widget-preloader__caller-details__details__specific__icon__preloader'></div>
                        <p className='ui-member-details-header-widget-preloader__caller-details__details__specific__title__preloader'></p>
                    </div>
                </div>
            </div>

            {/* family unit */}
            <div className='ui-member-details-header-widget-preloader__family-unit'>
                <div className='ui-member-details-header-widget-preloader__family-unit__member'>
                    <div className='ui-member-details-header-widget-preloader__family-unit__member__icon__preloader'></div>
                    <div className='ui-member-details-header-widget-preloader__family-unit__member__preloader'></div>
                </div>
                <div className='ui-member-details-header-widget-preloader__family-unit__member'>
                    <div className='ui-member-details-header-widget-preloader__family-unit__member__icon__preloader'></div>
                    <div className='ui-member-details-header-widget-preloader__family-unit__member__preloader'></div>
                </div>
                <div className='ui-member-details-header-widget-preloader__family-unit__member'>
                    <div className='ui-member-details-header-widget-preloader__family-unit__member__icon__preloader'></div>
                    <div className='ui-member-details-header-widget-preloader__family-unit__member__preloader'></div>
                </div>
            </div>

            {/* member savings */}
            <div className='ui-member-details-header-widget-preloader__savings'>
                <div className='ui-member-details-header-widget-preloader__savings__name'>
                </div>
                <div className='ui-member-details-header-widget-preloader__savings__details'>
                    <div className='ui-member-details-header-widget-preloader__savings__details__specific'>
                        <p className='ui-member-details-header-widget-preloader__savings__details__specific__title__preloader'></p>
                    </div>
                    <div className='ui-member-details-header-widget-preloader__savings__details__specific'>
                        <div className='ui-member-details-header-widget-preloader__savings__details__specific__icon__preloader'></div>
                    </div>
                </div>
            </div>

            <div className='ui-member-detail-assistive-actions-nav-widget-preloader'>
                { map( [ ...Array( 10 ).keys() ], item => {
                    return (
                        <div key={ item } className='ui-member-detail-assistive-actions-nav-widget-preloader__items'>
                            <div className='ui-member-detail-assistive-actions-nav-widget-preloader__items__item'></div>
                            <div className='ui-member-detail-assistive-actions-nav-widget-preloader__items__title'></div>
                        </div>
                    );
                } ) }
            </div>
        </div>
    );
};

// set display name
memberDetailsHeaderWidgetPreloader.displayName = 'memberDetailsHeaderWidget';

// set default props
memberDetailsHeaderWidgetPreloader.defaultProps = {};

// prop types
memberDetailsHeaderWidgetPreloader.propTypes = {};

/*******************************/

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { memberDetailsHeaderWidget as MemberDetailsHeaderWidget } from 'ui/views/widgets/memberDetailsHeaderWidget';
 *
 * // render
 * <MemberDetailsHeaderWidget propName='propValue'/>
 */
export const memberDetailsHeaderWidget = withNetworkState(
	memberDetailsHeaderWidgetPreloader,
	memberDetailsHeaderWidgetSuccess
);
